/**
 * EvaluationDialog - Performance evaluation form dialog
 * Extracted from HRPayrollPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Star } from "lucide-react";

const evaluationRatings = [
  { value: 5, label: "Excellent", color: "text-emerald-600" },
  { value: 4, label: "Good", color: "text-blue-600" },
  { value: 3, label: "Satisfactory", color: "text-amber-600" },
  { value: 2, label: "Needs Improvement", color: "text-orange-600" },
  { value: 1, label: "Unsatisfactory", color: "text-red-600" },
];

export function EvaluationDialog({
  open,
  onOpenChange,
  employees,
  newEvaluation,
  setNewEvaluation,
  saving,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" />
            New Performance Evaluation
          </DialogTitle>
          <DialogDescription>
            Create a performance review for an employee
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select
                  value={newEvaluation.employee_id}
                  onValueChange={(v) => setNewEvaluation(prev => ({ ...prev, employee_id: v }))}
                >
                  <SelectTrigger data-testid="eval-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Evaluation Date *</Label>
                <Input
                  type="date"
                  value={newEvaluation.evaluation_date}
                  onChange={(e) => setNewEvaluation(prev => ({ ...prev, evaluation_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Review Period Start</Label>
                <Input
                  type="date"
                  value={newEvaluation.period_start}
                  onChange={(e) => setNewEvaluation(prev => ({ ...prev, period_start: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Review Period End</Label>
                <Input
                  type="date"
                  value={newEvaluation.period_end}
                  onChange={(e) => setNewEvaluation(prev => ({ ...prev, period_end: e.target.value }))}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-slate-800 mb-3">Performance Ratings (1-5)</h4>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: "attendance_rating", label: "Attendance" },
                  { key: "punctuality_rating", label: "Punctuality" },
                  { key: "quality_of_work_rating", label: "Quality of Work" },
                  { key: "communication_rating", label: "Communication" },
                  { key: "teamwork_rating", label: "Teamwork" },
                  { key: "professionalism_rating", label: "Professionalism" },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <Label>{label}</Label>
                    <Select
                      value={String(newEvaluation[key])}
                      onValueChange={(v) => setNewEvaluation(prev => ({ ...prev, [key]: parseInt(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {evaluationRatings.map((r) => (
                          <SelectItem key={r.value} value={String(r.value)}>
                            {r.value} - {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Strengths</Label>
              <Textarea
                value={newEvaluation.strengths}
                onChange={(e) => setNewEvaluation(prev => ({ ...prev, strengths: e.target.value }))}
                placeholder="Employee's key strengths..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Areas for Improvement</Label>
              <Textarea
                value={newEvaluation.areas_for_improvement}
                onChange={(e) => setNewEvaluation(prev => ({ ...prev, areas_for_improvement: e.target.value }))}
                placeholder="Areas that need development..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Goals for Next Period</Label>
              <Textarea
                value={newEvaluation.goals}
                onChange={(e) => setNewEvaluation(prev => ({ ...prev, goals: e.target.value }))}
                placeholder="Goals and objectives..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Overall Comments</Label>
              <Textarea
                value={newEvaluation.overall_comments}
                onChange={(e) => setNewEvaluation(prev => ({ ...prev, overall_comments: e.target.value }))}
                placeholder="Additional comments..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Evaluation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
